import { useEffect, useMemo, useRef } from "react";
import type { Vehicle } from "./useVehicleStream";
import { useArrivals, type ArrivalMessage } from "./useArrivals";

type Props = {
  vehicles: Vehicle[];
  connected: boolean;
  /** True when the parent is showing a historical (scrubbed) snapshot. */
  historical?: boolean;
};

const EMPTY_VEHICLES: Vehicle[] = [];

export function formatTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDayDivider(ts: number): string {
  const d = new Date(ts * 1000);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const opts: Intl.DateTimeFormatOptions = sameDay
    ? { hour: "numeric", minute: "2-digit" }
    : { weekday: "short", hour: "numeric", minute: "2-digit" };
  return d.toLocaleString(undefined, opts);
}

// Group consecutive messages from the same train so we render one
// "contact" header followed by a bubble cluster, like a real SMS thread.
export type Group = {
  vehicleId: string;
  routeShortName?: string;
  routeColor?: string;
  tripHeadsign?: string;
  messages: ArrivalMessage[];
};

export function groupMessages(msgs: ArrivalMessage[]): Group[] {
  const groups: Group[] = [];
  for (const m of msgs) {
    const last = groups[groups.length - 1];
    if (
      last &&
      last.vehicleId === m.vehicleId &&
      m.ts - last.messages[last.messages.length - 1].ts < 15 * 60
    ) {
      last.messages.push(m);
      continue;
    }
    groups.push({
      vehicleId: m.vehicleId,
      routeShortName: m.routeShortName,
      routeColor: m.routeColor,
      tripHeadsign: m.tripHeadsign,
      messages: [m],
    });
  }
  return groups;
}

export function Messages({ vehicles, connected, historical = false }: Props) {
  // The arrivals message thread is only meaningful while live — accumulating
  // events across a scrub would mis-emit "arrived at X" lines. Freeze the
  // input so the existing thread stays intact, and surface the state with
  // a banner.
  const arrivalsInput = historical ? EMPTY_VEHICLES : vehicles;
  const { messages, error, ready } = useArrivals({ vehicles: arrivalsInput });
  const groups = useMemo(() => groupMessages(messages), [messages]);

  // Auto-scroll to the bottom when new messages arrive, but only if the
  // user is already near the bottom — don't yank them away from history
  // they're reading.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 80;
  }

  return (
    <div className="messages">
      <div className="messages-header">
        <div className="messages-title">🚆 Train SMS 💬</div>
        <div
          className={`messages-status ${historical ? "wait" : connected ? "ok" : "wait"}`}
        >
          {historical
            ? "◐ paused (history)"
            : connected
              ? "🟢 live"
              : "🟡 waiting…"}
        </div>
      </div>
      <div className="messages-thread" ref={scrollRef} onScroll={onScroll}>
        {historical && (
          <div className="messages-empty">
            ◐ Viewing a historical snapshot — the message thread stays paused
            here. Tap “Live” on the time slider to resume.
          </div>
        )}
        {error && (
          <div className="messages-empty">
            ⚠️ Couldn't load route geometry: {error}
          </div>
        )}
        {!error && !ready && (
          <div className="messages-empty">🗺️ Loading route geometry…</div>
        )}
        {!error && ready && messages.length === 0 && (
          <div className="messages-empty">
            ⏳ Waiting for the next train to arrive at a station… 🚉
          </div>
        )}
        {groups.map((g, gi) => {
          const first = g.messages[0];
          const prevGroup = groups[gi - 1];
          const showDivider =
            !prevGroup ||
            first.ts -
              prevGroup.messages[prevGroup.messages.length - 1].ts >
              30 * 60;
          const swatch = g.routeColor ? `#${g.routeColor}` : "#1f78ff";
          return (
            <div key={`${g.vehicleId}-${first.id}`} className="messages-group">
              {showDivider && (
                <div className="messages-divider">
                  {formatDayDivider(first.ts)}
                </div>
              )}
              <div className="messages-contact">
                <span
                  className="messages-avatar"
                  style={{ background: swatch }}
                  aria-hidden
                >
                  {g.routeShortName
                    ? g.routeShortName.slice(0, 2).toUpperCase()
                    : "?"}
                </span>
                <div className="messages-contact-meta">
                  <div className="messages-contact-name">
                    🚆 Train {g.vehicleId}
                  </div>
                  {g.tripHeadsign && (
                    <div className="messages-contact-sub">
                      {g.routeShortName ? `${g.routeShortName} · ` : ""}
                      ➡️ {g.tripHeadsign}
                    </div>
                  )}
                </div>
              </div>
              <div className="messages-bubbles">
                {g.messages.map((m, i) => (
                  <div key={m.id} className="messages-bubble-row">
                    {m.body && (
                      <div
                        className="messages-bubble"
                        style={{
                          // Accent the bubble with the route color, faintly.
                          boxShadow: `inset 3px 0 0 ${swatch}`,
                        }}
                      >
                        {m.body}
                      </div>
                    )}
                    {m.imageCaption && (
                      <div
                        className="messages-bubble messages-bubble-photo"
                        style={{ boxShadow: `inset 3px 0 0 ${swatch}` }}
                      >
                        {m.imageUrl ? (
                          <img
                            className="messages-photo"
                            src={m.imageUrl}
                            alt={`Photo near ${m.stopName}`}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className="messages-photo messages-photo-placeholder"
                            aria-hidden
                          />
                        )}
                        <div className="messages-photo-caption">
                          {m.imageCaption}
                        </div>
                      </div>
                    )}
                    {i === g.messages.length - 1 && (
                      <div className="messages-time">{formatTime(m.ts)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
